import type {
  ExtensionTargetModel,
  SchemaFieldDefinition,
} from "@erp/shared-types";
import { extensionIdentifier } from "@erp/shared-types";
import type { SocketType } from "../types";
import { socketTypeForField } from "./extractionSchemaToSockets";

export interface ProductExtensionInputSocketSpec {
  id: string;
  name: string;
  type: SocketType;
  required: boolean;
  description?: string;
}

/**
 * One registered entity extension, as a source of input sockets.
 *
 * Identified by what it logically IS — the entity it extends and the schema
 * defining it — rather than by the table backing it. Both are immutable, so a
 * socket id (which persisted edges reference) survives a slug rename, a
 * re-provision, or any other change to physical storage.
 */
export interface ProductExtensionSocketSource {
  targetModel: string;
  schemaDefinitionId: string;
  /** Schema name, used for the socket's display label. */
  extensionName: string;
  /** Fields of the version the provisioned table currently reflects. */
  fields: readonly SchemaFieldDefinition[];
}

/**
 * The static input sockets of `upsert_product`. Listed so socket generation can
 * assert extension fields stay clear of them.
 */
export const UPSERT_PRODUCT_RESERVED_INPUT_SOCKET_IDS = [
  "key",
  "dataSourceLink",
  "metadataId",
  "displaySku",
  "displayDescription",
  "upc",
  "isSellable",
  "isActive",
] as const;

/**
 * The static input sockets of `upsert_inventory_policy_classification`, for the
 * same reason as {@link UPSERT_PRODUCT_RESERVED_INPUT_SOCKET_IDS}.
 */
export const UPSERT_INVENTORY_POLICY_CLASSIFICATION_RESERVED_INPUT_SOCKET_IDS =
  [
    "productSku",
    "warehouseCode",
    "stockStatus",
    "popularityCode",
    "sourceType",
    "sourceWarehouseCode",
    "buyerCode",
    "coreOffer",
  ] as const;

/**
 * Steps that take one input socket per field of every extension registered for
 * the entity they write, keyed by step type. Every place that resolves, persists,
 * or writes extension sockets is driven from here, so a step listed here gets
 * them in the editor, on save, on read, and at run time.
 */
export const EXTENSION_SOCKET_STEPS = {
  upsert_product: {
    targetModel: "IM_PRODUCT_V3",
    reservedInputSocketIds: UPSERT_PRODUCT_RESERVED_INPUT_SOCKET_IDS,
  },
  upsert_inventory_policy_classification: {
    targetModel: "IM_INVENTORY_POLICY",
    reservedInputSocketIds:
      UPSERT_INVENTORY_POLICY_CLASSIFICATION_RESERVED_INPUT_SOCKET_IDS,
  },
} as const satisfies Record<
  string,
  {
    targetModel: ExtensionTargetModel;
    reservedInputSocketIds: readonly string[];
  }
>;

export type ExtensionSocketStepType = keyof typeof EXTENSION_SOCKET_STEPS;

/** The extension target a step's sockets draw from, or null when it takes none. */
export function extensionSocketStep(stepType: string): {
  targetModel: ExtensionTargetModel;
  reservedInputSocketIds: readonly string[];
} | null {
  return Object.prototype.hasOwnProperty.call(EXTENSION_SOCKET_STEPS, stepType)
    ? EXTENSION_SOCKET_STEPS[stepType as ExtensionSocketStepType]
    : null;
}

/**
 * Separates an extension's namespace from its field name in a socket id. A step's
 * own socket ids are plain identifiers and a field name is validated against
 * {@link SOCKET_FIELD_NAME_REGEX}, so neither can contain this character — which
 * makes a collision between an extension socket and a static one structurally
 * impossible, and makes "contains the separator" a sound test for "is an
 * extension socket".
 */
const NAMESPACE_SEPARATOR = ":";

// Extension field names are validated at authoring time
// (`assertValidExtensionField`); re-checked here because sockets are also
// generated from snapshots published before that rule existed.
const SOCKET_FIELD_NAME_REGEX = /^[A-Za-z][A-Za-z0-9_]*$/;

/**
 * Identifies one extension within a step's inputs — the same
 * {@link extensionIdentifier} that names its provisioned table, so the two can
 * never drift.
 *
 * A step draws from every extension registered for its entity, and two of them
 * may declare the same field name, so the field name alone can't identify a
 * socket. This is also how the write path routes a value back to its extension.
 */
export function extensionSocketNamespace(
  targetModel: string,
  schemaDefinitionId: string,
): string {
  return extensionIdentifier(targetModel, schemaDefinitionId);
}

/** `<targetModel>_<schemaDefinitionId>:<fieldName>`. */
export function productExtensionSocketId(
  targetModel: string,
  schemaDefinitionId: string,
  fieldName: string,
): string {
  return `${extensionSocketNamespace(targetModel, schemaDefinitionId)}${NAMESPACE_SEPARATOR}${fieldName}`;
}

/**
 * The (namespace, field) behind an extension socket id, or null when the id is
 * one of the step's own. The namespace is returned whole rather than split back
 * into target model and id: target models contain underscores, so re-splitting
 * would be ambiguous. Callers match it against
 * {@link extensionSocketNamespace} instead.
 */
export function parseProductExtensionSocketId(
  socketId: string,
): { namespace: string; fieldName: string } | null {
  const separator = socketId.indexOf(NAMESPACE_SEPARATOR);
  if (separator <= 0) return null;

  const namespace = socketId.slice(0, separator);
  const fieldName = socketId.slice(separator + 1);
  if (!fieldName) return null;
  return { namespace, fieldName };
}

/** Whether a persisted socket id belongs to an extension rather than the step. */
export function isProductExtensionSocketId(socketId: string): boolean {
  return parseProductExtensionSocketId(socketId) !== null;
}

/** `display_sku` / `displaySku` → `Display Sku`, matching the static sockets. */
function humanizeFieldName(fieldName: string): string {
  return fieldName
    .split(/[_\s]+|(?=[A-Z])/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * One input socket per top-level field of every registered extension.
 *
 * A socket is labelled by its FIELD — `Price`, not `Compliance · Price` — so the
 * node reads as one flat list of things to wire, the same as the step's own
 * inputs. The extension only enters the label to break a tie, since a node's
 * sockets are keyed by name on the canvas: a field that collides with a static
 * input or with another extension's field falls back to `<extension> · <field>`,
 * and the socket id (already unique) is the last resort. The tooltip always
 * names the extension.
 *
 * Every socket is `required: false` on purpose. Step readiness is an AND-join
 * (`isStepReady`), so a required-but-unwired socket would stall the whole
 * observe→upsert flow; a field's own `isRequired` is enforced at write time
 * instead. Child fields are skipped — they never become columns.
 *
 * Fields come from the version each extension's table actually reflects
 * (`provisioned_version_id`), so a socket can never describe a column that
 * doesn't exist.
 */
export function productExtensionFieldsToInputSockets(
  sources: readonly ProductExtensionSocketSource[],
  reservedInputSocketIds: readonly string[] = UPSERT_PRODUCT_RESERVED_INPUT_SOCKET_IDS,
): ProductExtensionInputSocketSpec[] {
  const takenNames = new Set(reservedInputSocketIds.map(humanizeFieldName));

  return sources.flatMap((source) =>
    source.fields
      .filter(
        (field) =>
          !field.parentFieldId && SOCKET_FIELD_NAME_REGEX.test(field.fieldName),
      )
      .map((field) => {
        const id = productExtensionSocketId(
          source.targetModel,
          source.schemaDefinitionId,
          field.fieldName,
        );
        const label = field.label || humanizeFieldName(field.fieldName);
        const qualified = `${source.extensionName} · ${label}`;
        const name = !takenNames.has(label)
          ? label
          : !takenNames.has(qualified)
            ? qualified
            : id;
        takenNames.add(name);

        return {
          id,
          name,
          type: socketTypeForField(field),
          required: false,
          description: field.description
            ? `${field.description} — extension "${source.extensionName}"`
            : `"${field.fieldName}" on extension "${source.extensionName}"`,
        };
      }),
  );
}
